/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1723507200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Aug 13 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "klaviyo",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Syncing events",
          "depth": 0,
          "id": "syncing-events"
        }, {
          "value": "Track configuration",
          "depth": 1,
          "id": "track-configuration"
        }, {
          "value": "Assigning events to profiles",
          "depth": 1,
          "id": "assigning-events-to-profiles"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Syncing lists",
          "depth": 0,
          "id": "syncing-lists"
        }, {
          "value": "Klaviyo lists",
          "depth": 1,
          "id": "klaviyo-lists"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "User identifiers",
          "depth": 1,
          "id": "user-identifiers"
        }, {
          "value": "User consent",
          "depth": 1,
          "id": "user-consent"
        }, {
          "value": "API changes",
          "depth": 0,
          "id": "api-changes"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      img: "img",
      em: "em",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User Profiles"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to your Klaviyo users."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/spawn_bulk_profile_import_job",
                children: "Batch profile docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync records as events to Klaviyo. This is often in the form of a track call."
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/create_event",
                children: "Track docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "List"
              })
            }), _jsx(_components.td, {
              children: "Add and remove users from lists without explicit consent."
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/create_list_relationships",
                children: "List docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subscription List"
              })
            }), _jsx(_components.td, {
              children: "Subscribe or unsubscribe profiles to email marketing, SMS marketing, or both,"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/reference/subscribe_profiles",
                children: "Subscribe Profiles docs"
              })
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The Klaviyo destination has been updated to latest version of the\n", _jsx(_components.a, {
            href: "https://developers.klaviyo.com/en/reference/api_overview",
            children: "API"
          }), ". There have\nbeen several changes that may affect syncs. See them ", _jsx(_components.a, {
            href: "#api-changes",
            children: "here"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To find your API keys, navigate to the ", _jsx(_components.a, {
          href: "https://www.klaviyo.com/settings/account/api-keys",
          children: "API keys page"
        }), " and create a private API key."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-api-keys.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch may require the following permissions based on which Sync Types you will be using."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Note: You cannot edit an API key after it's created."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Permissions (Read/Write)"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Events, Profiles"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User Profiles"
              })
            }), _jsx(_components.td, {
              children: "Profiles"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "List"
              })
            }), _jsx(_components.td, {
              children: "Lists, Profiles"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subscription List"
              })
            }), _jsx(_components.td, {
              children: "Lists, Profiles, Subscriptions"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-new-api-key.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Klaviyo workspace by either an email or phone number."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-record-match.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Klaviyo's default and custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-object-fieldmappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing events to Klaviyo and assigning them to a user."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "track-configuration",
        children: "Track configuration"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to choose the event name and event timestamp for a track event."
      }), "\n", _jsx(_components.p, {
        children: "Note: Hightouch accepts a normal date time format for the timestamp, and automatically convert it to the format that Klaviyo requires."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-track-configuration.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "assigning-events-to-profiles",
        children: "Assigning events to profiles"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to choose how you assign events to profiles. This is done by matching a column to a Klaviyo user identifier such as phone number or email."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-event-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to pass data to the event properties of a Klaviyo event."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-event-field-mappings.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-lists",
        children: "Syncing lists"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing profiles to Klaviyo Lists."
      }), "\n", _jsx(_components.p, {
        children: "Syncing profiles using emails or phone numbers to lists (regular and subscription lists) will automatically create any profiles that do not exist."
      }), "\n", _jsxs(_components.p, {
        children: ["Note: When adding profiles to regular lists a ", _jsx(_components.a, {
          href: "https://developers.klaviyo.com/en/reference/spawn_bulk_profile_import_job",
          children: "Batch Profile Job"
        }), " is used."]
      }), "\n", _jsx(_components.h3, {
        id: "klaviyo-lists",
        children: "Klaviyo lists"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports automatically creating a new list for your sync. You can specify a custom name for this list, or Hightouch will use the name of the model."
      }), "\n", _jsx(_components.p, {
        children: "Here, the model name List 1 will be used as the list name:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-lists-create.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Segment mode. New profiles will be inserted into the Klaviyo Lists. Profiles that are removed from the model will be removed from the list."
      }), "\n", _jsx(_components.h3, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["To identify which contacts to add or update in a list, select a model columns and the corresponding Klaviyo fields.\nYou can match on either ", _jsx(_components.strong, {
          children: "Email"
        }), " or ", _jsx(_components.strong, {
          children: "Phone number"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-klaviyo-record-match.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "user-consent",
        children: "User consent"
      }), "\n", _jsxs(_components.p, {
        children: ["Previously, user consent could be added with any profile update/create request, like in ", _jsx(_components.code, {
          children: "Events"
        }), ". Now, user consent is configured at the ", _jsx(_components.code, {
          children: "Subscription List"
        }), " level."]
      }), "\n", _jsx(_components.h2, {
        id: "api-changes",
        children: "API changes"
      }), "\n", _jsx(_components.p, {
        children: "After recent changes to the Klaviyo API, the following issues may affect your syncs."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Issue"
            }), _jsx(_components.th, {
              children: "Solution"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "API key permissions may not be correct"
            }), _jsxs(_components.td, {
              children: ["Create a new API key and asign correct permissions based on ", _jsx(_components.a, {
                href: "#setup",
                children: "this chart"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["Consent is no longer set on ", _jsx(_components.code, {
                children: "Events"
              })]
            }), _jsxs(_components.td, {
              children: ["Consent is setup through the use of ", _jsx(_components.code, {
                children: "Subscription Lists"
              }), " so it needs to be removed from any ", _jsx(_components.code, {
                children: "Event"
              }), " syncs. ", _jsx(_components.a, {
                href: "https://developers.klaviyo.com/en/docs/collect_email_and_sms_consent_via_api#subscribe-a-profile-via-api",
                children: "Klaviyo Docs"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Invalid phone numbers"
            }), _jsx(_components.td, {
              children: "Phone numbers should be in the format: +12345678901"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Invalid dates"
            }), _jsx(_components.td, {
              children: "Dates should be in ISO format"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Profile not found by ID"
            }), _jsx(_components.td, {
              children: "The ID field on profiles should only contain the Klaviyo generated profile ID"
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
